import { Loader } from '@googlemaps/js-api-loader'
export const GooglePlaces = {
  autocomplete: null,
  geocoder: null,
  /* `lastValidAddress` is used for tracking the value of the input field */
  lastValidAddress: null,
  loader: new Loader({
    apiKey: window.GOOGLE_PLACES_API_KEY,
    version: 'weekly',
    libraries: ['places'],
  }),
  /*
   * The `locationType` is used when pushing the location to the server,
   * with the `location` being nested under the `locationType` key.
   *
   * Ex.
   *   { "destination" : location }
   * */
  locationType: null,
  mounted() {
    // If the locationType is undefined it means there has been a developer
    // error. This is here mostly to protect future peoples and use-cases.
    if (this.el.dataset.locationType !== undefined) {
      this.el.addEventListener('clear_location_search', _event => {
        this.el.value = ''
        this.pushEvent('clear_destination', {})
      })
      this.locationType = this.el.dataset.locationType
      this.loader
        .load()
        .then(google => {
          const options = {
            fields: [
              'address_components',
              'formatted_address',
              'geometry',
              'name',
              'utc_offset_minutes',
            ],
            strictBounds: false,
          }

          this.autocomplete = new google.maps.places.Autocomplete(this.el, options)
          this.geocoder = new google.maps.Geocoder()

          this.autocomplete.addListener('place_changed', () => {
            this.placeChanged()
          })
        })
        .catch(e => {
          this.pushError(`Google maps failed to load! Error: ${e.message}`)
        })
    } else {
      this.pushError('Missing required data attribute: "data-location-type"')
    }
  },
  destroyed() {
    this.autocomplete.unbindAll()
  },
  pushError(message) {
    const error = { message }
    this.pushEvent('places_autocomplete_error', error)
  },
  buildLocation(result) {
    const location = {
      address2: null,
      address: result.formatted_address,
      city: null,
      country_iso: null,
      house_number: null,
      lat: result.geometry.location.lat().toString(),
      lng: result.geometry.location.lng().toString(),
      nickname: result.name,
      state: null,
      street_name: null,
      zipcode: null,
    }

    return result.address_components.reduce((acc, component) => {
      switch (component.types[0]) {
        case 'street_number':
          acc.house_number = component.short_name
          break
        case 'route':
          acc.street_name = component.short_name
          break
        case 'sublocality_level_1':
          acc.city = component.short_name
          break
        case 'locality':
          acc.city = component.short_name
          break
        case 'administrative_area_level_1':
          acc.state = component.short_name
          break
        case 'postal_code':
          acc.zipcode = component.short_name
          break
        case 'country':
          acc.country_iso = component.short_name
          break
        default:
          break
      }

      return acc
    }, location)
  },
  placeChanged() {
    const place = this.autocomplete.getPlace()

    /*
     * If the member presses enter without selecting something from the
     * autocomplete dropdown `place.address_components` will be undefined; so,
     * we need to guard against this before trying to build the location from
     * the place result.
     */
    if (place.address_components !== undefined) {
      const location = this.buildLocation(place)
      this.pushEventAddLocation(location)
    } else {
      /*
       * The user has pressed enter on the form, and there are two potential
       * scenarios that got us here:
       *
       * 1. The user selected a place out of the autocomplete and added a
       *    space to the address. So we'll first check if the `lastValidAddress`
       *    matches what's in the input, if they match we don't do anything.
       * 2. The user typed the address without selecting an option from the
       *    autocomplete. When this happens, we need to use Google's geocoding
       *    API to geocode the address and build the location.
       * */
      const inputValue = this.el.value.trim()

      if (this.lastValidAddress !== inputValue && inputValue !== '') {
        this.geocoder.geocode({ address: inputValue }, results => {
          // This assumes the first result is the one we want. There's a chance
          // it's not, but we don't have any UI to handle this case.
          const [result] = results
          const location = this.buildLocation(result)
          this.pushEventAddLocation(location)
        })
      }
    }
  },
  pushEventAddLocation(location) {
    const payload = {}
    payload[this.locationType] = location

    // Set the lastValidAddress before sending the payload
    this.lastValidAddress = this.el.value
    this.pushEvent('add_location', payload)
  },
}
